import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Search, Users, Heart, Eye, Building2, RefreshCw } from 'lucide-react';
import Paginacion, { paginar } from './Paginacion';
import axiosInstance from '../../utils/axiosConfig';

const API = '/api/calidad/sura-reporta';

const etiqueta = (v) => (v == null ? '' : typeof v === 'string' ? v : (v.label || v.nombre || v.id || ''));
const sinAcentos = (t) => (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const fecha = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Respuesta de la Seccion A: se muestra la etiqueta que manda el backend; si solo
// trae el id, se busca en el catalogo.
const textoDe = (valor, catalogo) => {
    if (valor && typeof valor === 'object') return etiqueta(valor);
    const item = (catalogo || []).find(c => c.id === valor);
    return item ? item.label : (valor || '');
};

const LIMITE_RESUMEN = 5;

const Resumen = ({ icon: Icon, titulo, conteo }) => {
    const [verTodos, setVerTodos] = useState(false);
    const total = conteo.reduce((t, c) => t + c.total, 0);
    const mayor = Math.max(1, ...conteo.map(c => c.total));
    const visibles = verTodos ? conteo : conteo.slice(0, LIMITE_RESUMEN);
    return (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Icon size={16} className="text-gray-400" />
                    {titulo}
                </h3>
            </div>
            {conteo.length === 0 ? (
                <p className="px-4 py-8 text-sm text-gray-400 text-center">Sin respuestas todavía</p>
            ) : (
                <div className="p-4 space-y-3">
                    {visibles.map(c => (
                        <div key={c.label}>
                            <div className="flex items-start justify-between gap-3 mb-1">
                                <span className="text-sm text-gray-700">{c.label}</span>
                                <span className="text-xs text-gray-500 shrink-0">
                                    <span className="font-semibold text-gray-900">{c.total}</span> · {total ? Math.round((c.total / total) * 100) : 0}%
                                </span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full bg-[#0062A5]" style={{ width: `${(c.total / mayor) * 100}%` }} />
                            </div>
                        </div>
                    ))}
                    {conteo.length > LIMITE_RESUMEN && (
                        <button
                            type="button"
                            onClick={() => setVerTodos(v => !v)}
                            className="text-xs font-medium text-sura-blue hover:underline"
                        >
                            {verTodos ? 'Ver menos' : `Ver todos (${conteo.length})`}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const contar = (lista, obtener) => {
    const mapa = new Map();
    lista.forEach(item => {
        const label = obtener(item);
        if (!label) return;
        mapa.set(label, (mapa.get(label) || 0) + 1);
    });
    return [...mapa.entries()].map(([label, total]) => ({ label, total })).sort((a, b) => b.total - a.total);
};

const CalidadConexiones = () => {
    const [conexiones, setConexiones] = useState([]);
    const [catalogos, setCatalogos] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busqueda, setBusqueda] = useState('');
    const [pagina, setPagina] = useState(1);

    const cargar = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [resCon, resCat] = await Promise.all([
                axiosInstance.get(`${API}/conexiones`),
                axiosInstance.get(`${API}/catalogos`).catch(() => ({ data: {} })),
            ]);
            const data = resCon.data;
            setConexiones(Array.isArray(data) ? data : (data.conexiones || []));
            setCatalogos(resCat.data || {});
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudieron cargar las respuestas');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const filas = useMemo(() => conexiones.map(c => {
        // Departamento real que la persona tenia en CentroSura al responder.
        const depto = c.departamento_label || c.departamento || 'Sin departamento';
        return {
            ...c,
            _persona: etiqueta(c.persona) || c.empleado || c.usuario || 'Sin nombre',
            _deptoReal: etiqueta(c.persona?.departamento_real || c.departamento_real),
            _depto: depto,
            _deptoBase: depto,
            _significado: textoDe(c.significado_label || c.significado, catalogos.significados),
            _habito: textoDe(c.habito_label || c.habito, catalogos.habitos),
        };
    }), [conexiones, catalogos]);

    const visibles = useMemo(() => {
        const q = sinAcentos(busqueda.trim());
        if (!q) return filas;
        return filas.filter(f => sinAcentos(`${f._persona} ${f._deptoReal} ${f._depto}`).includes(q));
    }, [filas, busqueda]);

    useEffect(() => { setPagina(1); }, [busqueda]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 size={32} className="animate-spin text-gray-400" />
            </div>
        );
    }
    if (error) {
        return (
            <div className="text-center py-16">
                <p className="text-gray-500">{error}</p>
                <button onClick={cargar} className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-sura-blue hover:underline">
                    <RefreshCw size={14} /> Reintentar
                </button>
            </div>
        );
    }
    if (filas.length === 0) {
        return (
            <div className="text-center py-20">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users size={32} className="text-gray-300" />
                </div>
                <p className="text-gray-500 text-lg">Nadie ha respondido la Conexión con la Calidad todavía</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Resumen icon={Building2} titulo="Departamento" conteo={contar(filas, f => f._deptoBase)} />
                <Resumen icon={Heart} titulo="Qué representa la calidad" conteo={contar(filas, f => f._significado)} />
                <Resumen icon={Eye} titulo="Hábito en anaquel" conteo={contar(filas, f => f._habito)} />
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="p-4 border-b border-gray-100 flex items-center gap-3 flex-wrap">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Users size={16} className="text-gray-400" />
                        Respuestas por colaborador
                        <span className="text-xs font-normal text-gray-400">({filas.length})</span>
                    </h3>
                    <div className="relative ml-auto w-full sm:w-72">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={busqueda}
                            onChange={e => setBusqueda(e.target.value)}
                            placeholder="Buscar por persona o departamento"
                            className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:border-blue-500"
                        />
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                                <th className="px-4 py-3">Colaborador</th>
                                <th className="px-4 py-3">Departamento</th>
                                <th className="px-4 py-3">Qué representa la calidad</th>
                                <th className="px-4 py-3">Hábito en anaquel</th>
                                <th className="px-4 py-3 whitespace-nowrap">Respondió</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {paginar(visibles, pagina).map((f, i) => (
                                <tr key={f.id ?? i} className="align-top hover:bg-gray-50/60">
                                    <td className="px-4 py-3">
                                        <p className="font-medium text-gray-900">{f._persona}</p>
                                    </td>
                                    <td className="px-4 py-3 text-gray-700">{f._depto || '-'}</td>
                                    <td className="px-4 py-3 text-gray-700 min-w-[220px]"><p className="line-clamp-2" title={f._significado}>{f._significado || '-'}</p></td>
                                    <td className="px-4 py-3 text-gray-700 min-w-[220px]"><p className="line-clamp-2" title={f._habito}>{f._habito || '-'}</p></td>
                                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fecha(f.created_at)}</td>
                                </tr>
                            ))}
                            {visibles.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-gray-400">Sin resultados para la búsqueda</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <Paginacion pagina={pagina} total={visibles.length} onCambiar={setPagina} />
            </div>
        </div>
    );
};

export default CalidadConexiones;
